// Cave84 — supplier-invoice import: shared types and pure helpers.
//
// Every supplier parser (MWC, Tomer Gal, later Burgundy Crown and iDealwine)
// turns its own PDF layout into one SupplierInvoice. The import page
// (pages/InvoiceImportPage.tsx) only ever sees this shape, so matching against
// the catalog, the review table, FX and the Firestore writes are written once.
//
// Pure: no React, no Firebase — unit-tested in invoiceImport.test.ts.

import type {
  AppellationInference, BottleFormat, Currency, Producer, PurchaseSource, Vintage, Wine, WineStyle,
} from '@cave84/shared-types';
import { cuveeScore, distinct, producerKey, tokens } from './catalogKeys';
import type { ParsedInvoice, Reconciliation } from './mwcInvoiceParser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One wine line of a supplier invoice → `qty` bottle docs on import. */
export interface ImportLine {
  /** Unique within the invoice (MWC article number, Tomer Gal line number). */
  key: string;
  /** Short reference shown in the review table ("Lot 149", "#3"). */
  ref: string;
  qty: number;
  /** The line's price as printed on the invoice, in the invoice currency
   *  (MWC: the lot's hammer price; Tomer Gal: line total incl. VAT). */
  linePrice: number;
  /** Landed cost per bottle in the invoice currency, allocated from the
   *  invoice's own totals (fees, discount, VAT). */
  costPerBottle: number;
  producerRaw: string;      // as printed on the invoice
  producerName: string;     // normalised
  year: number;             // NV = 1001
  cuvee: string;
  inferredStyle: WineStyle;
  inferredAppellation: AppellationInference;
  inOwc: boolean;
  format: BottleFormat;
  /** Free text written to every bottle's `notes`. */
  bottleNote: string;
}

/**
 * An invoice line that will NOT become bottles. Never dropped silently: every
 * one is listed on the review screen with its amount and the reason, and the
 * reconciliation counts it so the invoice total still balances.
 */
export interface ExcludedLine {
  ref: string;
  description: string;
  amount: number;
  /** 'not_wine' = recognised as a non-wine charge (tasting, delivery, …).
   *  'unparsed' = looked like a wine but could not be split into
   *  producer / vintage / cuvée → blocks import until overridden. */
  kind: 'not_wine' | 'unparsed';
  reason: string;
}

export interface SupplierInvoice {
  lines: ImportLine[];
  excluded: ExcludedLine[];
  currency: Currency;
  invoiceNumber: string;
  /** DD.MM.YYYY */
  invoiceDate: string;
  /** Written to the PurchaseLot doc. */
  purchaseLotNote: string;
  reconciliation: Reconciliation;
}

export interface SupplierImporter {
  /** URL slug: /import/<id> */
  id: string;
  name: string;
  /** "Auction · Germany" */
  kind: string;
  description: string;
  source: PurchaseSource;
  currency: Currency;
  /** Column header for ImportLine.linePrice in the review table. */
  linePriceLabel: string;
  status: 'ready' | 'coming_soon';
  /** Text extracted from the PDF (see pdfItemsToPageText) → invoice. Throws
   *  when the PDF is not this supplier's invoice. */
  parse?: (text: string) => SupplierInvoice;
}

/** `purchase_source_detail` on every bottle of the invoice. Also the key the
 *  duplicate-import guard looks for. */
export const invoiceSourceDetail = (invoiceNumber: string) => `Invoice ${invoiceNumber}`;

// ---------------------------------------------------------------------------
// MWC adapter
// ---------------------------------------------------------------------------

export function fromMwcInvoice(p: ParsedInvoice): SupplierInvoice {
  return {
    lines: p.lots.map(l => ({
      key: l.article,
      ref: `Lot ${l.lot}`,
      qty: l.qty,
      linePrice: l.lotPriceEur,
      costPerBottle: l.pricePerBottleEur,
      producerRaw: l.producerRaw,
      producerName: l.producerName,
      year: l.year,
      cuvee: l.cuvee,
      inferredStyle: l.inferredStyle,
      inferredAppellation: l.inferredAppellation,
      inOwc: l.inOwc,
      format: '750ml' as const,
      bottleNote: `MWC Lot ${l.lot} — Article ${l.article}`
        + (l.inGiftBox ? ' — in gift carton (Geschenkkarton)' : ''),
    })),
    excluded: p.unparsedLines.map((text, i) => ({
      ref: `?${i + 1}`,
      description: text,
      amount: 0,
      kind: 'unparsed' as const,
      reason: 'Lot line found but producer / vintage / wine could not be read',
    })),
    currency: 'EUR',
    invoiceNumber: p.meta.invoiceNumber,
    invoiceDate: p.meta.invoiceDate,
    purchaseLotNote: `MWC Invoice #${p.meta.invoiceNumber} — Bidder ${p.meta.bidder}`,
    reconciliation: p.reconciliation,
  };
}

// ---------------------------------------------------------------------------
// Catalog matching
// ---------------------------------------------------------------------------

type MatchInput = Pick<ImportLine, 'producerName' | 'producerRaw' | 'cuvee' | 'year'>;
export type CatalogProducer = Pick<Producer, 'id' | 'name' | 'aliases'>;
export type CatalogWine = Pick<Wine, 'id' | 'producer_id' | 'cuvee_name' | 'vineyard_specific'>;
export type CatalogVintage = Pick<Vintage, 'id' | 'wine_id' | 'year'>;

export interface CatalogSnapshot {
  producers: CatalogProducer[];
  wines: CatalogWine[];
  vintages: CatalogVintage[];
}

export type Matched<T> = T & {
  existingVintageId: string | null;
  existingWineId: string | null;
  existingProducerId: string | null;
  isNew: boolean;
  /** Set when the line cannot be matched with certainty (e.g. the producer
   *  exists under two names). Import is BLOCKED while any selected line has
   *  one — the importer never guesses, because a guess silently creates a
   *  duplicate. */
  matchProblem?: string;
};

/**
 * Match every line to the catalog using the shared keys in utils/catalogKeys:
 *   producer — same producerKey as the name or an alias ("Ch. Pavie Decesse" =
 *              "Château Pavie Decesse"). Two different producers with that key
 *              = duplicate in the catalog → matchProblem, import blocked.
 *   wine     — best cuvée score > 0.5 among that producer's wines; a tie →
 *              matchProblem. (A substring match made "Chambertin" match
 *              "Charmes-Chambertin" and missed "Ch." vs "Château".)
 */
export function matchLots<T extends MatchInput>(
  lots: T[],
  producers: CatalogProducer[],
  wines: CatalogWine[],
  vintages: CatalogVintage[],
): Matched<T>[] {
  return lots.map(lot => {
    const key = producerKey(lot.producerName);
    const rawKey = producerKey(lot.producerRaw);
    const hits = producers.filter(p =>
      [p.name, ...(p.aliases ?? [])].some(n => { const k = producerKey(n); return k === key || k === rawKey; }));
    if (hits.length > 1) {
      return { ...lot, existingProducerId: null, existingWineId: null, existingVintageId: null, isNew: true,
        matchProblem: `Producer exists under ${hits.length} names (${hits.map(p => `"${p.name}"`).join(' / ')}). `
          + 'Merge them first (npm run merge:producers), then re-upload the invoice.' };
    }
    const producer = hits[0];

    let wineId: string | null = null;
    let matchProblem: string | undefined;
    if (producer) {
      const exclude = new Set([...tokens(producer.name), ...tokens(lot.producerName)]);
      const a = distinct(lot.cuvee, exclude);
      const scored = wines
        .filter(w => w.producer_id === producer.id)
        .map(w => ({ w, s: cuveeScore(a, distinct([w.cuvee_name, w.vineyard_specific].filter(Boolean).join(' '), exclude)) }))
        .filter(x => x.s > 0.5)
        .sort((x, y) => y.s - x.s);
      if (scored.length > 1 && scored[1]!.s === scored[0]!.s) {
        matchProblem = `"${lot.cuvee}" matches several wines of ${producer.name}: `
          + scored.filter(x => x.s === scored[0]!.s).map(x => `"${x.w.cuvee_name}"`).join(' / ') + '. Merge the duplicate wines first.';
      } else {
        wineId = scored[0]?.w.id ?? null;
      }
    }
    const vintage = wineId ? vintages.find(v => v.wine_id === wineId && v.year === lot.year) : undefined;
    return {
      ...lot,
      existingProducerId: producer?.id ?? null,
      existingWineId: wineId,
      existingVintageId: vintage?.id ?? null,
      isNew: !vintage,
      ...(matchProblem ? { matchProblem } : {}),
    };
  });
}

/**
 * Existing producers that an UNMATCHED invoice name probably means. Local
 * importers print short names — "Lafon", "Leflaive", "Roulot" — and when the
 * short name's words are all contained in a catalog producer's name (or the
 * other way round) the owner must choose: use that producer, or create a new
 * one. Never decided automatically: "Lafon" can be Comtes Lafon or Dominique
 * Lafon. Empty when nothing overlaps.
 */
export function producerCandidates(name: string, producers: CatalogProducer[]): CatalogProducer[] {
  const q = producerKey(name).split(' ').filter(Boolean);
  if (q.length === 0) return [];
  const qs = new Set(q);
  return producers.filter(p => [p.name, ...(p.aliases ?? [])].some(n => {
    const c = producerKey(n).split(' ').filter(Boolean);
    if (c.length === 0) return false;
    const cs = new Set(c);
    return q.every(t => cs.has(t)) || c.every(t => qs.has(t));
  }));
}

/** True when `name` is already the producer's name or one of its aliases. */
export function producerKnowsName(p: CatalogProducer, name: string): boolean {
  const k = producerKey(name);
  return [p.name, ...(p.aliases ?? [])].some(n => producerKey(n) === k);
}

// ---------------------------------------------------------------------------
// Dates & FX
// ---------------------------------------------------------------------------

/** "17.10.2026" → "2026-10-17"; '' when unreadable. */
export function isoFromDotted(d: string): string {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(d.trim());
  if (!m) return '';
  return `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`;
}

/** Bottle cost in EUR from the invoice-currency cost and an EUR-per-unit rate. */
export function toEur(amount: number, eurPerUnit: number): number {
  return Math.round(amount * eurPerUnit * 100) / 100;
}
